/**
 * API(주로 axios) 에러 처리를 위한 공통 타입과 헬퍼.
 *
 * 배경: 프로젝트 전반의 createAsyncThunk가 `catch (error: any)` 후
 *   error.response?.data?.message || error.response?.data?.error
 *   || error.message || "기본 메시지"
 * 형태로 메시지를 추출하고 있었다(약 86곳). 이 정형 패턴을 타입 안전한
 * 단일 헬퍼로 대체해 `any` 사용을 줄인다.
 *
 * 설계: axios를 직접 import하지 않고 구조적(structural) 타입 가드로
 * 필요한 필드만 확인한다. 따라서 axios 버전/인스턴스에 의존하지 않으며,
 * axios 이외의 에러(일반 Error, 문자열 등)도 안전하게 처리한다.
 */

/** 서버가 반환하는 에러 응답 본문의 흔한 형태 */
export interface ApiErrorResponseData {
  message?: string;
  error?: string;
  detail?: string;
  [key: string]: unknown;
}

/** axios 유사 에러의 최소 형태 */
export interface ApiError {
  message?: string;
  /** 일부 커스텀 에러 객체는 top-level에 detail/error를 둔다 */
  detail?: string;
  error?: string;
  response?: {
    status?: number;
    data?: ApiErrorResponseData;
  };
}

/** 값이 객체인지 확인 (null 제외) */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/**
 * unknown 에러에서 axios 유사 구조를 좁혀 반환한다.
 * 구조가 맞지 않으면 빈 객체를 반환한다.
 */
export const toApiError = (error: unknown): ApiError => {
  if (!isRecord(error)) return {};

  const apiError: ApiError = {};

  if (typeof error.message === "string") {
    apiError.message = error.message;
  }
  if (typeof error.detail === "string") {
    apiError.detail = error.detail;
  }
  if (typeof error.error === "string") {
    apiError.error = error.error;
  }

  const response = error.response;
  if (isRecord(response)) {
    const data = response.data;
    apiError.response = {
      status: typeof response.status === "number" ? response.status : undefined,
      data: isRecord(data) ? (data as ApiErrorResponseData) : undefined,
    };
  }

  return apiError;
};

/**
 * unknown 에러에서 사람이 읽을 메시지를 추출한다.
 * 우선순위: response.data.message → response.data.error →
 *           response.data.detail → error.message → fallback
 */
export const getApiErrorMessage = (
  error: unknown,
  fallback = "An error occurred"
): string => {
  const apiError = toApiError(error);
  const data = apiError.response?.data;

  return (
    data?.message ||
    data?.error ||
    data?.detail ||
    apiError.message ||
    apiError.detail ||
    apiError.error ||
    fallback
  );
};

/** unknown 에러에서 HTTP 상태 코드를 추출한다(없으면 undefined). */
export const getApiErrorStatus = (error: unknown): number | undefined =>
  toApiError(error).response?.status;
